// Bandeja de solicitudes del portal del empleado (staff).
// Nómina → Movimientos → Solicitudes del portal. Aprobar ejecuta la acción
// real (goce de vacaciones, anticipo pendiente o novedad de permiso).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { ExportarBotones, type Celda } from '@/lib/utils/exportarTabla';
import { Campo, Modal, fecha, fieldCls, money, nomRpc, TIPOS_SOLICITUD, type SolicitudRow } from './shared';
import { fechaLocal } from '@/lib/utils/fecha';
import { LOCALE } from '@/lib/utils/regional';

const ESTADO_SOL: Record<string, string> = { pendiente: 'bg-pendiente-soft text-pendiente', aprobada: 'bg-estable-soft text-estable', rechazada: 'bg-urgente-soft text-urgente' };

function detalle(s: SolicitudRow): string {
  if (s.tipo === 'vacaciones') return `${s.datos.desde} → ${s.datos.hasta}`;
  if (s.tipo === 'anticipo') return `${money(Number(s.datos.monto))}${s.datos.motivo ? ` — ${s.datos.motivo}` : ''}`;
  return `${s.datos.fecha} (${s.datos.dias} día/s)${s.datos.motivo ? ` — ${s.datos.motivo}` : ''}`;
}

export function SolicitudesPortal() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState('pendiente');
  const { data: rows = [], isFetching } = useQuery<SolicitudRow[]>({
    queryKey: ['nom-solicitudes', estado],
    queryFn: () => nomRpc<SolicitudRow[]>('nom_solicitudes', { _estado: estado }),
    staleTime: 0,
  });

  const [resolviendo, setResolviendo] = useState<{ s: SolicitudRow; aprobar: boolean } | null>(null);
  const [respuesta, setRespuesta] = useState('');

  const resolver = useMutation({
    mutationFn: () => nomRpc('nom_solicitud_resolver', { _id: resolviendo!.s.id, _aprobar: resolviendo!.aprobar, _respuesta: respuesta || null }),
    onSuccess: () => {
      toast.success(resolviendo!.aprobar ? 'Solicitud aprobada y aplicada' : 'Solicitud rechazada');
      setResolviendo(null);
      void qc.invalidateQueries({ queryKey: ['nom-solicitudes'] });
      void qc.invalidateQueries({ queryKey: ['nom-anticipos'] });
      void qc.invalidateQueries({ queryKey: ['nom-novedades'] });
      void qc.invalidateQueries({ queryKey: ['nom-vacaciones'] });
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Exportar (CSV / XLS / PDF) — respeta el filtro actual. El detalle es texto (montos embebidos) → sin fila de TOTAL.
  const headersExp = ['N°', 'Fecha', 'Empleado', 'Tipo', 'Detalle', 'Estado', 'Respuesta'];
  const filasExport: Celda[][] = rows.map((s, i): Celda[] => [
    i + 1, fecha(s.created_at), `${s.razon_social ?? ''}${s.identificacion ? ` (${s.identificacion})` : ''}`,
    TIPOS_SOLICITUD[s.tipo], detalle(s), s.estado, s.respuesta ?? '',
  ]);

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Solicitudes del portal</h1>
      <select className={`${fieldCls} w-56`} value={estado} onChange={(e) => setEstado(e.target.value)} aria-label="Estado">
        <option value="pendiente">Pendientes</option>
        <option value="aprobada">Aprobadas</option>
        <option value="rechazada">Rechazadas</option>
        <option value="todos">Todas</option>
      </select>

      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
          <CardTitle className="text-base">Solicitudes ({rows.length})</CardTitle>
          <ExportarBotones
            nombre={`solicitudes-portal-${fechaLocal()}`} titulo="Solicitudes del portal"
            headers={headersExp} filas={filasExport} colsDerecha={[0]}
            subtitulo={`${new Date().toLocaleString(LOCALE)} · ${rows.length} solicitud(es)`} disabled={rows.length === 0} />
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Empleado</th><th className="py-2 pr-3">Tipo</th>
              <th className="py-2 pr-3">Detalle</th><th className="py-2 pr-3">Estado</th><th className="py-2 pr-3">Respuesta</th><th className="py-2"></th>
            </tr></thead>
            <tbody>
              {rows.length === 0 && !isFetching && <tr><td colSpan={7} className="py-8 text-center text-muted-foreground">Sin solicitudes</td></tr>}
              {rows.map((s) => (
                <tr key={s.id} className="border-b last:border-0 hover:bg-muted/40">
                  <td className="py-2 pr-3 font-mono">{fecha(s.created_at)}</td>
                  <td className="py-2 pr-3">{s.razon_social}<div className="text-xs text-muted-foreground font-mono">{s.identificacion}</div></td>
                  <td className="py-2 pr-3">{TIPOS_SOLICITUD[s.tipo]}</td>
                  <td className="py-2 pr-3">{detalle(s)}</td>
                  <td className="py-2 pr-3"><span className={`px-2 py-0.5 rounded-full text-xs ${ESTADO_SOL[s.estado]}`}>{s.estado}</span></td>
                  <td className="py-2 pr-3 text-muted-foreground">{s.respuesta}</td>
                  <td className="py-2 text-right whitespace-nowrap">
                    {s.estado === 'pendiente' && (
                      <>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-estable hover:bg-estable-soft" title="Aprobar"
                          onClick={() => { setResolviendo({ s, aprobar: true }); setRespuesta(''); }}><Check className="w-4 h-4" /></button>
                        <button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-destructive hover:bg-destructive/10 ml-1" title="Rechazar"
                          onClick={() => { setResolviendo({ s, aprobar: false }); setRespuesta(''); }}><X className="w-4 h-4" /></button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {resolviendo && (
        <Modal title={`${resolviendo.aprobar ? 'Aprobar' : 'Rechazar'} solicitud — ${resolviendo.s.razon_social}`} onClose={() => setResolviendo(null)}>
          <div className="space-y-3">
            <div className="text-sm">{TIPOS_SOLICITUD[resolviendo.s.tipo]}: {detalle(resolviendo.s)}</div>
            {resolviendo.aprobar && (
              <div className="text-xs text-muted-foreground">
                Al aprobar se {resolviendo.s.tipo === 'vacaciones' ? 'registra el goce de vacaciones (valida el saldo)'
                  : resolviendo.s.tipo === 'anticipo' ? 'crea el anticipo pendiente (se descuenta en el próximo rol)'
                  : 'registra la novedad de permiso'}.
              </div>
            )}
            <Campo label="Respuesta para el empleado"><input className={fieldCls} value={respuesta} onChange={(e) => setRespuesta(e.target.value)} /></Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setResolviendo(null)}>Cancelar</Button>
              <Button variant={resolviendo.aprobar ? 'default' : 'destructive'} isLoading={resolver.isPending} onClick={() => resolver.mutate()}>
                {resolviendo.aprobar ? 'Aprobar y aplicar' : 'Rechazar'}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
